import { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import {
  Loading,
  Flex,
  FlexColumn,
  EmptyStateBox,
} from '@elementary/common-ui/components';
import {
  AI_USAGE,
  AI_USAGE_PERSONAL,
} from '@elementary/common-ui/constants/queryKeys';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import * as api from 'shared/services/settingsApi';
import CreditsCard from './CreditsCard';
import AiSpendByDayChart from './AiSpendByDayChart';
import UsageCard from './UsageCard';
import RunTypeFilter from './RunTypeFilter';
import RangeToggle from './RangeToggle';
import { VisibleRunTypes } from './types';
import { useAiUsageViewModel } from './useAiUsageViewModel';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  gap: 24px;
  padding-bottom: 32px;
`;

const AiUsage = () => {
  const hasAccountPermission = useAccountPermission();
  const canViewAccountUsage = hasAccountPermission('VIEW_AI_USAGE');
  const [range, setRange] = useState<api.SpendRange>('3m');
  const [visibleTypes, setVisibleTypes] = useState<VisibleRunTypes>({
    agent: true,
    ci: true,
  });

  const {
    data: personal,
    isLoading,
    isError,
  } = useQuery([AI_USAGE_PERSONAL], () => api.fetchPersonalAiUsage());

  const {
    data: adminData,
    isLoading: isAdminLoading,
    isError: isAdminError,
  } = useQuery([AI_USAGE, range], () => api.fetchAiUsage(range), {
    keepPreviousData: true,
    enabled: canViewAccountUsage,
  });

  const viewModel = useAiUsageViewModel(adminData);

  if (isLoading) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }
  if (isError || !personal) {
    return (
      <Wrapper>
        <EmptyStateBox title="Failed to load AI usage">
          Something went wrong while loading AI usage. Please try again.
        </EmptyStateBox>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <CreditsCard
        title="Your daily credit usage"
        subtitle="Personal — your own usage today"
        creditsUsed={personal.daily_credits_used}
        creditLimit={personal.daily_credit_limit}
        resetsText={`Resets daily at ${localizeDate(
          personal.daily_resets_at,
        ).format('LT')}.`}
        limitReachedText="Daily credit limit reached."
      />
      {canViewAccountUsage && isAdminError && (
        <EmptyStateBox title="Failed to load account usage">
          Something went wrong while loading account-wide AI usage. Please try
          again.
        </EmptyStateBox>
      )}
      {canViewAccountUsage && isAdminLoading && <Loading />}
      {canViewAccountUsage && !isAdminError && adminData && (
        <>
          <CreditsCard
            title="Account-wide monthly credit usage"
            subtitle="All users across the account this month"
            creditsUsed={adminData.credits_used}
            creditLimit={adminData.credit_limit}
            resetsText={`Resets on ${localizeDate(adminData.resets_at).format(
              'LL',
            )}, at ${localizeDate(adminData.resets_at).format('LT')}.`}
            limitReachedText="Credit limit reached."
          />
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <RunTypeFilter value={visibleTypes} onChange={setVisibleTypes} />
            <RangeToggle value={range} onChange={setRange} />
          </Flex>
          <AiSpendByDayChart
            data={viewModel.spend}
            visibleTypes={visibleTypes}
          />
          <UsageCard
            leaderboard={viewModel.leaderboard}
            sessions={viewModel.sessions}
          />
        </>
      )}
    </Wrapper>
  );
};

export default AiUsage;
